import type { InternalUiSpec } from "./catalog";

/**
 * A whole-page dashboard described by a single json-render spec: @erp/ui
 * primitives (cards, badges, table), an @erp/viz chart (the `Chart` component),
 * and an interactive action — the "Create reorder" button binds `on.press` to
 * the `createReorder` action, submitting the `$state`-bound form fields. The
 * host (demo page / chat widget / workflow surface) supplies the matching
 * handler at render time.
 */
export const internalUiDemoSpec: InternalUiSpec = {
  root: "page",
  elements: {
    page: {
      type: "Stack",
      props: { gap: "lg", padding: "lg" },
      children: ["hero", "kpis", "chartCard", "ordersCard", "reorderCard"],
    },

    hero: {
      type: "Card",
      props: {
        title: "Procurement overview",
        description:
          "A full page — cards, chart, table, and a live action — from one json-render spec.",
      },
      children: ["heroText"],
    },
    heroText: {
      type: "Text",
      props: {
        value: "Rendered with @erp/ui and @erp/viz. Color signals status only.",
        variant: "muted",
      },
      children: [],
    },

    kpis: {
      type: "Grid",
      props: { columns: 3, gap: "md" },
      children: ["kpiOpen", "kpiSpend", "kpiOnTime"],
    },
    kpiOpen: {
      type: "Card",
      props: {},
      children: ["kpiOpenLabel", "kpiOpenValue"],
    },
    kpiOpenLabel: {
      type: "Text",
      props: { value: "Open POs", variant: "eyebrow" },
      children: [],
    },
    kpiOpenValue: {
      type: "Heading",
      props: { value: "128", level: 2 },
      children: [],
    },
    kpiSpend: {
      type: "Card",
      props: {},
      children: ["kpiSpendLabel", "kpiSpendValue"],
    },
    kpiSpendLabel: {
      type: "Text",
      props: { value: "Spend MTD", variant: "eyebrow" },
      children: [],
    },
    kpiSpendValue: {
      type: "Heading",
      props: { value: "$1.24M", level: 2 },
      children: [],
    },
    kpiOnTime: {
      type: "Card",
      props: {},
      children: ["kpiOnTimeLabel", "kpiOnTimeValue"],
    },
    kpiOnTimeLabel: {
      type: "Text",
      props: { value: "On-time", variant: "eyebrow" },
      children: [],
    },
    kpiOnTimeValue: {
      type: "Heading",
      props: { value: "94%", level: 2 },
      children: [],
    },

    chartCard: {
      type: "Card",
      props: { title: "Spend by month" },
      children: ["spendChart"],
    },
    spendChart: {
      type: "Chart",
      props: {
        height: 240,
        spec: {
          mark: { type: "bar", cornerRadiusEnd: 3 },
          data: {
            values: [
              { month: "Jan", spend: 820000 },
              { month: "Feb", spend: 910000 },
              { month: "Mar", spend: 1040000 },
              { month: "Apr", spend: 980000 },
              { month: "May", spend: 1180000 },
              { month: "Jun", spend: 1240000 },
            ],
          },
          encoding: {
            x: { field: "month", type: "nominal", title: null },
            y: { field: "spend", type: "quantitative", title: "Spend ($)" },
          },
        },
      },
      children: [],
    },

    ordersCard: {
      type: "Card",
      props: { title: "Recent orders" },
      children: ["ordersTable"],
    },
    ordersTable: {
      type: "Table",
      props: {
        columns: [
          { key: "po", label: "PO" },
          { key: "vendor", label: "Vendor" },
          { key: "status", label: "Status" },
          { key: "total", label: "Total", align: "right" },
        ],
        rows: { $state: "/orders" },
      },
      children: [],
    },

    reorderCard: {
      type: "Card",
      props: {
        title: "Quick reorder",
        description: "Submits the form data to the createReorder action.",
      },
      children: ["reorderStack"],
    },
    reorderStack: {
      type: "Stack",
      props: { gap: "sm" },
      children: [
        "skuLabel",
        "skuInput",
        "qtyLabel",
        "qtyInput",
        "reorderButton",
      ],
    },
    skuLabel: {
      type: "Text",
      props: { value: "SKU", variant: "label" },
      children: [],
    },
    skuInput: {
      type: "Input",
      props: { placeholder: "BRC-1042", value: { $bindState: "/reorder/sku" } },
      children: [],
    },
    qtyLabel: {
      type: "Text",
      props: { value: "Quantity", variant: "label" },
      children: [],
    },
    qtyInput: {
      type: "Input",
      props: {
        type: "number",
        placeholder: "24",
        value: { $bindState: "/reorder/qty" },
      },
      children: [],
    },
    reorderButton: {
      type: "Button",
      props: { label: "Create reorder", variant: "default" },
      on: {
        press: {
          action: "createReorder",
          params: {
            sku: { $state: "/reorder/sku" },
            qty: { $state: "/reorder/qty" },
          },
        },
      },
      children: [],
    },
  },
};

/** Initial state for {@link internalUiDemoSpec}. */
export const internalUiDemoState: Record<string, unknown> = {
  reorder: { sku: "", qty: "" },
  orders: [
    {
      po: "PO-1042",
      vendor: "Acme Foods",
      status: "Approved",
      total: "$4,200.00",
    },
    {
      po: "PO-1043",
      vendor: "Northwind Trading",
      status: "Pending",
      total: "$1,890.00",
    },
    {
      po: "PO-1044",
      vendor: "Birchwood Mills",
      status: "Approved",
      total: "$12,540.00",
    },
    { po: "PO-1045", vendor: "Cedar & Co", status: "Draft", total: "$640.00" },
  ],
};
